import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Bot, UserSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AssistantPermission {
  asistente_id: string;
  grupo_id: string;
  created_at: string;
  asistente: {
    nombre: string;
    modelo: string;
    estado: string;
  };
  grupo: {
    nombre: string;
    role: string;
  };
}

interface Assistant {
  id: string;
  nombre: string;
  modelo: string;
  estado: string;
}

interface UserGroup {
  id: string;
  nombre: string;
  role: string;
}

export default function AssistantPermissions() {
  const [permissions, setPermissions] = useState<AssistantPermission[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newPermission, setNewPermission] = useState({
    asistente_id: '',
    grupo_id: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPermissions();
    fetchAssistants();
    fetchGroups();
  }, []);

  const fetchPermissions = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes_grupos')
        .select(`
          asistente_id,
          grupo_id,
          created_at,
          asistente:asistentes!asistentes_grupos_asistente_id_fkey(nombre, modelo, estado),
          grupo:grupos_usuarios!asistentes_grupos_grupo_id_fkey(nombre, role)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPermissions(data || []);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Error al cargar los permisos');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssistants = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes')
        .select('id, nombre, modelo, estado')
        .order('nombre');

      if (error) throw error;
      setAssistants(data || []);
    } catch (error) {
      console.error('Error fetching assistants:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('grupos_usuarios')
        .select('id, nombre, role')
        .order('nombre');

      if (error) throw error;
      setGroups(data || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('asistentes_grupos')
        .insert([{
          asistente_id: newPermission.asistente_id,
          grupo_id: newPermission.grupo_id
        }]);

      if (error) throw error;
      setSuccess('Permiso creado correctamente');
      setShowModal(false);
      setNewPermission({ asistente_id: '', grupo_id: '' });
      fetchPermissions();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDelete = async (asistente_id: string, grupo_id: string) => {
    if (!confirm('¿Estás seguro de eliminar este permiso? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('asistentes_grupos')
        .delete()
        .eq('asistente_id', asistente_id)
        .eq('grupo_id', grupo_id);

      if (error) throw error;
      setSuccess('Permiso eliminado correctamente');
      fetchPermissions();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const filteredPermissions = permissions.filter(permission =>
    permission.asistente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.grupo.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Permisos de Asistentes</h1>
        <button 
          onClick={() => {
            setNewPermission({ asistente_id: '', grupo_id: '' });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Permiso
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar permisos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold text-gray-600">Asistente</th>
                  <th className="pb-3 font-semibold text-gray-600">Modelo</th>
                  <th className="pb-3 font-semibold text-gray-600">Grupo</th>
                  <th className="pb-3 font-semibold text-gray-600">Rol del Grupo</th>
                  <th className="pb-3 font-semibold text-gray-600">Estado</th>
                  <th className="pb-3 font-semibold text-gray-600">Creado</th>
                  <th className="pb-3 font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPermissions.map((permission) => (
                  <tr key={`${permission.asistente_id}-${permission.grupo_id}`} className="border-b">
                    <td className="py-3">
                      <div className="flex items-center">
                        <Bot className="w-5 h-5 text-blue-500 mr-2" />
                        {permission.asistente.nombre}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="text-sm font-mono">{permission.asistente.modelo}</span>
                    </td>
                    <td className="py-3">
                      <div className="flex items-center">
                        <UserSquare className="w-5 h-5 text-gray-500 mr-2" />
                        {permission.grupo.nombre}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        permission.grupo.role === 'administrador'
                          ? 'bg-purple-100 text-purple-800'
                          : permission.grupo.role === 'usuario_avanzado'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {permission.grupo.role === 'administrador'
                          ? 'Administrador'
                          : permission.grupo.role === 'usuario_avanzado'
                          ? 'Usuario Avanzado'
                          : 'Usuario Estándar'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded-full text-sm ${
                        permission.asistente.estado === 'activo'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {permission.asistente.estado}
                      </span>
                    </td>
                    <td className="py-3">
                      {new Date(permission.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDelete(permission.asistente_id, permission.grupo_id)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                          title="Eliminar permiso"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPermissions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500">
                      <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron permisos</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para añadir permiso */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Nuevo Permiso
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asistente
                </label>
                <select
                  value={newPermission.asistente_id}
                  onChange={(e) => setNewPermission({...newPermission, asistente_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Seleccionar asistente</option>
                  {assistants.map((assistant) => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.nombre} ({assistant.modelo})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grupo
                </label>
                <select
                  value={newPermission.grupo_id}
                  onChange={(e) => setNewPermission({...newPermission, grupo_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Seleccionar grupo</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.nombre} ({group.role})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  Crear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}